<template>
  <v-modal
    id="show-case-connect-luggage-modal"
    class-name="modal-fullscreen-sm-down w-auto"
    size="lg"
    @closed="emit('closed')"
  >
    <modal-header>
      <modal-title>{{ t('tourClient.connectLuggage') }}</modal-title>
    </modal-header>

    <modal-body>
      <div v-if="loading" class="d-flex justify-content-center py-40">
        <v-loader />
      </div>
      <div v-else-if="!connections || connections.length === 0" class="text-center py-40">
        <v-icon icon="info-circle" size="xl" class="text-muted mb-16" />
        <p class="h6 text-muted mb-8">No Itinerary Data Found</p>
        <p class="text-sm text-muted mb-0">We couldn't detect any hotel transitions for case #{{ props.caseId }}.</p>
      </div>
      <div v-else>
        <div class="mb-16">
          <p class="text-sm text-muted mb-0">Review hotel transitions. Rows in green are already connected.</p>
        </div>

        <div class="table-responsive">
          <table class="table table-sm align-middle mb-0 compact-table">
            <thead>
              <tr class="bg-light text-uppercase x-small fw-bold text-muted border-top border-bottom">
                <th class="ps-16 py-10" style="width: 140px;">Day & Date</th>
                <th class="py-10">From Hotel</th>
                <th class="py-10 pe-16">To Hotel</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="conn in connections" 
                :key="conn.day_order" 
                class="border-bottom"
                :class="{'bg-connected': conn.status === 'connected'}"
              >
                <td class="ps-16 py-12">
                  <div class="fw-bold text-dark text-sm">Day {{ conn.day_order }}</div>
                  <div class="text-xs text-muted">{{ conn.day_date }}</div>
                </td>

                <td class="py-12">
                  <div v-if="conn.origin_hotel_name">
                    <div class="text-sm fw-bold text-dark text-truncate max-w-200">
                      {{ conn.origin_hotel_name }}
                    </div>
                    <div class="text-xs text-muted d-flex align-items-center gap-4">
                      <v-icon icon="location-dot" size="xs" />
                      <span class="text-truncate">{{ conn.origin_location_name || 'No location' }}</span>
                    </div>
                  </div>
                  <div v-else class="text-muted text-xs italic">N/A</div>
                </td>

                <td class="py-12 pe-16">
                  <div class="d-flex justify-content-between align-items-center gap-12">
                    <div v-if="conn.destination_hotel_name" class="flex-grow-1 min-w-0">
                      <div 
                        class="text-sm fw-bold text-truncate max-w-200" 
                        :class="conn.status === 'valid' ? 'text-primary' : 'text-dark'"
                      >
                        {{ conn.destination_hotel_name }}
                      </div>
                      <div class="text-xs text-muted d-flex align-items-center gap-4">
                        <v-icon icon="location-dot" size="xs" />
                        <span class="text-truncate">{{ conn.destination_location_name || 'No location' }}</span>
                      </div>
                      <div v-if="conn.status === 'skipped'" class="text-xs text-danger fw-medium mt-4">
                        {{ conn.reason }}
                      </div>
                    </div>
                    <div v-else class="text-muted text-xs italic">N/A</div>

                    <div class="d-flex align-items-center gap-8 flex-shrink-0">
                      <v-icon 
                        v-if="conn.has_transport" 
                        icon="luggage-cart" 
                        size="sm" 
                        class="text-info" 
                        :title="conn.status === 'connected' ? 'Connected' : 'Ready to connect'"
                      />
                      <v-icon 
                        v-if="conn.status === 'connected'" 
                        icon="check-circle" 
                        size="sm" 
                        class="text-success" 
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </modal-body>

    <modal-footer class="d-flex justify-content-between align-items-center mt-12 px-20 pb-20 border-0">
      <v-button 
        class="btn-sm btn-outline-secondary px-24 fw-bold text-uppercase x-small" 
        @click="emit('closed')"
      >
        {{ t('tourBooking.cancel') }}
      </v-button>
      <v-button 
        class="btn-sm btn-primary btn-icon px-24 fw-bold text-uppercase x-small" 
        :disabled="loading || !hasValidConnections" 
        @click="connectLuggage"
      > 
        <span>{{ t('general.shared.confirm') }}</span>
        <v-icon icon="check" size="xs" space="ms-8" />
      </v-button>
    </modal-footer>
  </v-modal>
</template>

<script setup>
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import ModalFooter from "@/components/vendor/basic/modal/ModalFooter.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import api from "@/api";
import { useToast } from "vue-toastification";
import { onMounted, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const toast = useToast();
const emit = defineEmits(["closed", "loadClientCases"]);
const props = defineProps({
  caseId: {
    type: [String, Number],
    required: true,
  },
  agency: {
    type: String,
    required: true,
  },
});

const loading = ref(true);
const connections = ref([]);

const hasValidConnections = computed(() => {
  return connections.value?.some(conn => ['valid', 'connected'].includes(conn.status));
});

const fetchPreview = async () => {
  if (!props.caseId || !props.agency) return;

  try {
    loading.value = true;
    const response = await api.case.connectLuggagePreview(props.agency, props.caseId);
    
    let extractedData = [];
    
    // Deep inspection of response
    if (response) {
      // 1. Check if response is the data array itself
      if (Array.isArray(response)) {
        extractedData = response;
      } 
      // 2. Check for standard Laravel/Axios wrapping
      else if (response.data) {
        if (Array.isArray(response.data)) {
          extractedData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          extractedData = response.data.data;
        } else if (typeof response.data === 'object') {
          // Fallback for associative arrays
          extractedData = Object.values(response.data).filter(v => typeof v === 'object' && v !== null);
        }
      }
      // 3. Last ditch: look for any array in the response object
      else if (typeof response === 'object') {
        const potentialArray = Object.values(response).find(val => Array.isArray(val));
        if (potentialArray) extractedData = potentialArray;
      }
    }

    connections.value = [...extractedData];
  } catch (e) {
    console.error("Connect Luggage Preview Error:", e);
    toast.error(t("general.shared.errorMessage"));
  } finally {
    loading.value = false;
  }
};

watch(() => props.caseId, (newId) => {
  if (newId) fetchPreview();
}, { immediate: true });

const connectLuggage = async () => {
  try {
    const response = await api.case.caseToConnectLuggage(props.agency, props.caseId);
    toast.success(response.message || t("general.shared.savedChanges"));
    emit("loadClientCases");
    emit("closed");
  } catch (e) {
    console.error("Connect Luggage Submit Error:", e);
    toast.error(t("general.shared.errorMessage"));
  }
};
</script>

<style scoped>
.x-small { font-size: 0.7rem; }
.bg-connected { background-color: rgba(40, 167, 69, 0.08) !important; }
.compact-table th { background-color: #f9fafb; }
.compact-table td { border-color: #f3f4f6; }
.max-w-200 { max-width: 200px; }
.min-w-0 { min-width: 0; }
</style>
